import {computed, extendObservable} from 'mobx';
import {Context, EMPTY_CONTEXT, Options, Schema} from './schema';

export type TupleType = any[];
export type ElementsSchemas = Schema[];

export interface TupleOptions<T extends TupleType> extends Options<T | null> {}

// TypeScript does not allow accessing element types of a tuple; as such, the
// creation of tuple schemas requires the specification of its type arguments.
// As an example, a tuple whose elements' schema is
// `[StringSchema, NumberSchema]` may be created with:
// ```
// new TupleSchema<
//   [string | null, number | null],
//   [StringSchema, NumberSchema]
// >([new StringSchema(), new NumberSchema()])
// ```

export class TupleSchema<
  T extends TupleType = TupleType,
  S extends ElementsSchemas = ElementsSchemas
> extends Schema<T | null, TupleOptions<T>> {
  private _elementsSchemas: S;

  constructor(
    // Force type parameter `T` to be inferred from the elements' schema
    elementsSchemas: S & ElementsSchemas,
    options: TupleOptions<T> = {},
  ) {
    super(options);
    this._elementsSchemas = elementsSchemas;
  }

  public toJSON(): object {
    return {
      type: 'TupleSchema',
      ...super.toJSON(),
      elementsSchemas: this._elementsSchemas,
    };
  }

  public get elementsSchemas(): S {
    return this._elementsSchemas;
  }

  // When defined, merge the schema's initial value with its elements'
  public initialValue(context: Context = EMPTY_CONTEXT): T | null {
    const schemaInitialValue = super.initialValue(context);
    return schemaInitialValue === null
      ? null
      : this._merge(this.elementsInitialValues(context), schemaInitialValue);
  }

  // Initial value for all elements of the tuple
  public elementsInitialValues(context: Context = EMPTY_CONTEXT): T {
    // The initial value of a tuple's element may access the initial value of
    // previous elements (we use `reduce` instead of `map` for this)
    return this._elementsSchemas.reduce((tuple: any, _, i) => {
      tuple.push(this.elementInitialValue(tuple, i, context));
      return tuple;
    }, []) as T;
  }

  // Initial value for element with index `index` of `value`
  public elementInitialValue(
    value: T,
    index: number,
    context: Context = EMPTY_CONTEXT,
  ) {
    return this._elementsSchemas[index].initialValue(
      this._buildChildContext(value, index, context),
    );
  }

  public valueToMobx(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): T | null {
    const tuple = this._nullifyValue(value);
    if (tuple === null) {
      return null;
    }
    // Return a MobX object with derived properties for each element that has
    // computed properties
    const mobxTuple: any = [];
    this._elementsSchemas.forEach((schema, i) => {
      const childContext = this._buildChildContext(mobxTuple, i, context);
      extendObservable(mobxTuple, {
        [i]: schema.isComputed
          ? computed(() =>
              schema.valueToMobx(
                schema.computedValue(childContext),
                childContext,
              ),
            )
          : schema.valueToMobx(tuple[i], childContext),
      });
    });
    return mobxTuple;
  }

  public elementToMobx(
    value: T | null,
    index: number,
    elementValue: any,
    context: Context = EMPTY_CONTEXT,
  ): any {
    return this._elementsSchemas[index].valueToMobx(
      elementValue,
      this._buildChildContext(value, index, context),
    );
  }

  public valueToJS(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): T | null {
    const tuple = this.isComputed
      ? this.computedValue(context)
      : this._nullifyValue(value);
    if (tuple === null) {
      return null;
    }
    // Return a lazy record with "getters" for each computed element
    const lazyTuple: any = [];
    this._elementsSchemas.forEach((schema, i) => {
      if (schema.isComputed) {
        Object.defineProperty(lazyTuple, `${i}`, {
          enumerable: true,
          configurable: true,
          get: () =>
            schema.valueToJS(
              null,
              this._buildChildContext(lazyTuple, i, context),
            ),
        });
      } else {
        lazyTuple[i] = schema.valueToJS(
          tuple[i],
          this._buildChildContext(lazyTuple, i, context),
        );
      }
    });
    return lazyTuple;
  }

  public typeIsValid(value: any, strict?: boolean): value is T | null {
    return (
      value === null ||
      (Array.isArray(value) &&
        value.length === this._elementsSchemas.length &&
        value.every((element, i) =>
          this._elementsSchemas[i].typeIsValid(element, strict),
        ))
    );
  }

  public valueIsValid(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      this.requiredIsValid(value, context) &&
      this.elementsAreValid(value, context) &&
      this.customFunctionIsValid(value, context)
    );
  }

  public elementIsValid(
    value: T,
    index: number,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      index >= this._elementsSchemas.length ||
      this._elementsSchemas[index].valueIsValid(
        value[index],
        this._buildChildContext(value, index, context),
      )
    );
  }

  public elementsAreValid(
    value: T | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      value === null ||
      value.every((_, i) => this.elementIsValid(value, i, context))
    );
  }
}
